'use client';

import { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';

interface UpgradeFeature {
    icon: string;
    title: string;
    description: string;
}

interface UpgradeSuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    planName?: string;
    accountType?: 'personal' | 'business';
}

// Personal features - one per slide
const personalFeatures: UpgradeFeature[] = [
    {
        icon: '4k',
        title: 'Stunning 4K Quality',
        description:
            'Experience art in crystal-clear resolution on any screen, without watermarks.',
    },
    {
        icon: 'playlist',
        title: 'Unlimited Playlists',
        description:
            'Create and curate art collections for every mood, room, or occasion.',
    },
    {
        icon: 'heart',
        title: 'Full Art Library',
        description:
            'Access thousands of works from world-class artists, updated regularly.',
    },
    {
        icon: 'app',
        title: 'Complete Control',
        description:
            'Manage your screens from anywhere using the Niio app on your phone.',
    },
];

// Business features - one per slide
const businessFeatures: UpgradeFeature[] = [
    {
        icon: 'license',
        title: 'Commercial License',
        description:
            'Display art legally in any commercial space with full licensing included.',
    },
    {
        icon: 'upload',
        title: 'Custom Content',
        description:
            'Upload your own branded content and mix it with curated art.',
    },
    {
        icon: 'multi',
        title: 'Multi-Screen Control',
        description:
            'Manage all your screens from one dashboard, anywhere in the world.',
    },
    {
        icon: '4k',
        title: 'Professional 4K',
        description:
            'Stream in stunning 4K quality without watermarks on every screen.',
    },
];

// Feature icon component matching PaymentWallModal/LastChanceModal
function FeatureIcon({ type }: { type: string }) {
    const iconClass = 'w-10 h-10 text-purple-400';

    switch (type) {
        case '4k':
            return (
                <span className="w-10 h-10 flex items-center justify-center text-sm font-bold text-purple-400 bg-purple-400/20 rounded-lg">
                    4K
                </span>
            );
        case 'playlist':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                    />
                </svg>
            );
        case 'heart':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                </svg>
            );
        case 'app':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                </svg>
            );
        case 'license':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                </svg>
            );
        case 'upload':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                </svg>
            );
        case 'multi':
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
            );
        default:
            return (
                <svg
                    className={iconClass}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M5 13l4 4L19 7"
                    />
                </svg>
            );
    }
}

/**
 * Celebration modal shown after successful plan upgrade
 * Features a carousel showcasing one benefit per slide
 */
export function UpgradeSuccessModal({
    isOpen,
    onClose,
    planName = 'Premium',
    accountType = 'personal',
}: UpgradeSuccessModalProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    // Select features based on account type
    const features =
        accountType === 'business' ? businessFeatures : personalFeatures;

    // Auto-advance carousel
    useEffect(() => {
        if (!isOpen || features.length <= 1) return;

        const interval = setInterval(() => {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentSlide((prev) => (prev + 1) % features.length);
                setIsAnimating(false);
            }, 300);
        }, 3500);

        return () => clearInterval(interval);
    }, [isOpen, features.length]);

    // Reset slide and trigger celebration when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentSlide(0);

            // Premium confetti celebration
            const celebrationColors = [
                '#a855f7', // purple-500
                '#c084fc', // purple-400
                '#fbbf24', // amber-400
                '#f59e0b', // amber-500
                '#ffffff', // white
                '#e879f9', // fuchsia-400
            ];

            const randomInRange = (min: number, max: number) => {
                return Math.random() * (max - min) + min;
            };

            // Initial burst from center
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                colors: celebrationColors,
                zIndex: 100,
            });

            // Side bursts
            setTimeout(() => {
                confetti({
                    particleCount: 50,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0 },
                    colors: celebrationColors,
                    zIndex: 100,
                });
                confetti({
                    particleCount: 50,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1 },
                    colors: celebrationColors,
                    zIndex: 100,
                });
            }, 200);

            // Continuous gentle confetti
            const duration = 3000;
            const animationEnd = Date.now() + duration;

            const interval = setInterval(() => {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    clearInterval(interval);
                    return;
                }

                const particleCount = 15 * (timeLeft / duration);

                confetti({
                    particleCount,
                    startVelocity: 20,
                    spread: 360,
                    origin: {
                        x: randomInRange(0.2, 0.8),
                        y: randomInRange(0.1, 0.4),
                    },
                    colors: celebrationColors,
                    gravity: 0.8,
                    ticks: 100,
                    zIndex: 100,
                });
            }, 300);

            return () => clearInterval(interval);
        }
    }, [isOpen]);

    const goToSlide = useCallback(
        (index: number) => {
            if (index === currentSlide) return;
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentSlide(index);
                setIsAnimating(false);
            }, 300);
        },
        [currentSlide]
    );

    if (!isOpen) return null;

    const currentFeature = features[currentSlide];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-700">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
                    aria-label="Close"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                {/* Content */}
                <div className="p-6 sm:p-8">
                    {/* Success Icon */}
                    <div className="flex justify-center mb-5">
                        <div className="relative">
                            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
                                <svg
                                    className="w-8 h-8 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            {/* Subtle ring */}
                            <div className="absolute inset-0 rounded-full border-2 border-green-400/30 animate-ping" />
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
                        Welcome to {planName}!
                    </h2>

                    {/* Subtitle */}
                    <p className="text-gray-400 text-center text-sm mb-6">
                        Your art experience just got upgraded
                    </p>

                    {/* Feature Carousel - Single feature per slide */}
                    <div className="bg-white/5 rounded-xl p-6 mb-6 min-h-[180px] border border-white/10">
                        <div
                            className={`flex flex-col items-center text-center transition-opacity duration-300 ${
                                isAnimating ? 'opacity-0' : 'opacity-100'
                            }`}
                        >
                            {/* Large Icon */}
                            <div className="mb-4 p-3 bg-purple-500/10 rounded-xl">
                                <FeatureIcon type={currentFeature.icon} />
                            </div>

                            {/* Feature Title */}
                            <h3 className="text-lg font-semibold text-white mb-2">
                                {currentFeature.title}
                            </h3>

                            {/* Feature Description */}
                            <p className="text-gray-400 text-sm leading-relaxed">
                                {currentFeature.description}
                            </p>
                        </div>
                    </div>

                    {/* Dots indicator */}
                    {features.length > 1 && (
                        <div className="flex justify-center gap-2 mb-6">
                            {features.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToSlide(index)}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        index === currentSlide
                                            ? 'bg-purple-500 w-6'
                                            : 'bg-gray-600 hover:bg-gray-500 w-2'
                                    }`}
                                />
                            ))}
                        </div>
                    )}

                    {/* CTA Button */}
                    <button
                        onClick={onClose}
                        className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-full transition-colors shadow-lg shadow-purple-500/25"
                    >
                        START EXPLORING
                    </button>

                    {/* Tip */}
                    <p className="text-gray-500 text-xs text-center mt-4">
                        Tip: Download the Niio app to control your screens
                        anywhere
                    </p>
                </div>
            </div>
        </div>
    );
}

export default UpgradeSuccessModal;
